import {
  FlagBannerFold,
  GearFine,
  Layout,
  Lightning,
  Palette,
  UserCircleDashed,
  Users,
  WebhooksLogo,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import { Banner } from "features/layout/components";

import DesktopWorkspaceSelector from "common/components/DesktopWorkspaceSelector";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { UserPermissions } from "common/types";

import AgencyPlan from "../components/AgencyPlan";
import ApiKeys from "../components/ApiKeys";
import AvatarPlan from "../components/AvatarPlan";
import Clients from "../components/Clients";
import General from "../components/General";
import LifetimeSeats from "../components/LifetimeSeats";
import Members from "../components/Members";
import OutreachPlan from "../components/OutreachPlan";
import ProfileSettings from "../components/ProfileSettings";
import Webhooks from "../components/Webhooks";
import Whitelabel from "../components/Whitelabel";
import useAgencySettings from "../datahooks/useAgencySettings";

import type { JSX, ReactNode } from "react";

import AvatarIcon from "assets/icons/plans/avatar-dark.svg?react";

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  component: JSX.Element;
  actionComponent?: JSX.Element;
  visibleForPermissions?: UserPermissions;
  bannerComponent?: JSX.Element;
}

interface NavItemComponent {
  element: ReactNode;
  key?: string;
}

export default function useSettingsNavItems() {
  const isDesktop = useTwBreakpoint("lg");

  const { permissions, role, agency_subscription_status } =
    useSelectedWorkspaceContext();
  const { appSumoPromotion, ltdPromotion } = usePromotions();
  const { agencySettings } = useAgencySettings();

  const hasAgencyPlan =
    agency_subscription_status && agency_subscription_status !== "cancelled";

  let navItems: (NavItem | NavItemComponent)[] = [];
  const agencyNavItems: (NavItem | NavItemComponent)[] = [
    {
      path: "agency",
      label: isDesktop ? "Billing & seats" : "Agency plan",
      icon: <Lightning weight="fill" className="size-4" />,
      component: <AgencyPlan />,
    },
  ];
  let ltdNavItem: NavItem;

  if (!getWhitelabelCustomization()) {
    if (isDesktop) {
      navItems.unshift({
        key: "workspace-selector",
        element: <DesktopWorkspaceSelector isExpanded />,
      });
    } else {
      agencyNavItems.unshift({
        key: "agency-mobile-divider",
        element: (
          <div className="w-px shrink-0 my-auto mx-2.5 h-5 bg-black-400" />
        ),
      });
    }
    navItems.push(
      {
        path: "general",
        label: "General",
        icon: <Layout weight="fill" />,
        component: <General />,
      },
      {
        path: "outreach-plan",
        label: "Outreach billing",
        icon: <Lightning weight="fill" />,
        component: <OutreachPlan />,
        visibleForPermissions: ["billing_full", "billing_limited"],
      },
      {
        path: "avatar-plan",
        label: "Avatar billing",
        icon: <AvatarIcon />,
        component: <AvatarPlan />,
        visibleForPermissions: ["billing_full", "billing_limited"],
      },
      {
        path: "members",
        label: "Members",
        icon: <Users weight="fill" />,
        component: <Members />,
        visibleForPermissions: ["members_full", "members_limited"],
      },
    );
    if (role === "owner" && hasAgencyPlan) {
      navItems.push({
        path: "clients",
        label: "Clients",
        icon: <UserCircleDashed weight="fill" />,
        component: <Clients />,
        bannerComponent: !agencySettings && (
          <Banner variant="warning">
            <p className="lg:truncate">
              You must configure your whitelabel branding before inviting
              clients
            </p>
            <Button
              variant="link-black"
              className="ml-auto lg:border-l border-black-200"
              asChild
            >
              <Link to="./whitelabel">Go to whitelabel settings</Link>
            </Button>
          </Banner>
        ),
      });
    }
    navItems.push(
      {
        path: "api",
        label: "API keys",
        icon: <GearFine weight="fill" />,
        component: <ApiKeys />,
        visibleForPermissions: ["public_api_full", "public_api_limited"],
      },
      {
        path: "webhooks",
        label: "Webhooks",
        icon: <WebhooksLogo weight="fill" />,
        component: <Webhooks />,
        visibleForPermissions: ["webhooks_full"],
      },
    );
    if (role === "owner") {
      if (hasAgencyPlan) {
        agencyNavItems.push({
          path: "whitelabel",
          label: "White label",
          icon: <Palette weight="fill" />,
          component: <Whitelabel />,
        });
      }
      if (ltdPromotion || appSumoPromotion) {
        ltdNavItem = {
          path: "ltd",
          label: "Lifetime seats",
          icon: <FlagBannerFold weight="fill" />,
          component: <LifetimeSeats />,
        };
      }
    }
  }

  //filter items based on permissions
  navItems = navItems.filter((item) => {
    if ("element" in item) {
      return true;
    }
    const { visibleForPermissions } = item;
    return visibleForPermissions
      ? visibleForPermissions.some((permission) =>
          permissions.includes(permission),
        )
      : true;
  });

  const routes = [
    {
      path: "",
      label: "Profile settings",
      component: <ProfileSettings />,
    },
    ...navItems,
    ...agencyNavItems,
  ].filter((item) => !("element" in item)) as NavItem[];

  if (ltdNavItem) {
    routes.push(ltdNavItem);
  }

  return { routes, navItems, agencyNavItems, ltdNavItem };
}
